import React, { useRef, useEffect, useState } from 'react';
import warning from 'tiny-warning';
function useDebounce(callBack, debounceTime) {
  var timeoutRef = useRef();
  useEffect(function () {
    return function () {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  var debouncedFunc = function debouncedFunc() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(function () {
      callBack.apply(void 0, args);
    }, debounceTime);
  };
  return debouncedFunc;
}
var useRandomId = function useRandomId(prefix) {
  var ref = React.useRef(String(Math.random()).substring(2));
  return prefix + "-" + ref.current;
};
function useOnMount(callback) {
  var hasRun = React.useRef(false);
  React.useEffect(function () {
    if (!hasRun.current) {
      hasRun.current = true;
      callback();
    }
  }, [callback]);
}
var mergeRefs = function mergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return function (node) {
    for (var _i = 0, _refs = refs; _i < _refs.length; _i++) {
      var ref = _refs[_i];
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) ref.current = node;
    }
  };
};
var useForceUpdate = function useForceUpdate() {
  var _useState = useState(0),
    setValue = _useState[1];
  return function () {
    return setValue(function (value) {
      return value + 1;
    });
  };
};
var useOnClickOutside = function useOnClickOutside(refs, handler) {
  useEffect(function () {
    var listener = function listener(event) {
      // If the ref contains the clicked element, then the click is not outside
      if (refs.some(function (ref) {
        return elementContainsEventTarget(ref.current, event);
      })) {
        return;
      }
      handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return function () {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [refs, handler]);
};
var elementContainsEventTarget = function elementContainsEventTarget(element, event) {
  if (!element) {
    return false;
  }
  if (element.contains(event.target)) {
    return true;
  }
  // For elements inside a Shadow DOM we need to check the composedPath
  if (event.composed && event.composedPath) {
    var contains = event.composedPath().find(function (target) {
      if (target === window) {
        return false;
      }
      return element.contains(target);
    });
    return contains ? true : false;
  }
  return false;
};
var useOnEscape = function useOnEscape(ref, handler) {
  useEffect(function () {
    var runIfKeyIsEscape = function runIfKeyIsEscape(event) {
      if (event.key === 'Escape') handler();
    };
    var currentRef = ref.current;
    currentRef == null ? void 0 : currentRef.addEventListener('keydown', runIfKeyIsEscape);
    return function () {
      return currentRef == null ? void 0 : currentRef.removeEventListener('keydown', runIfKeyIsEscape);
    };
  }, [ref, handler]);
};

// from https://stackoverflow.com/questions/36862334/get-viewport-window-height-in-reactjs
var getWindowDimensions = function getWindowDimensions() {
  if (typeof window === 'undefined') return {
    width: undefined,
    height: undefined
  };
  var _window = window,
    width = _window.innerWidth,
    height = _window.innerHeight;
  return {
    width: width,
    height: height
  };
};
var useWindowDimensions = function useWindowDimensions() {
  var _useState = useState(getWindowDimensions()),
    windowDimensions = _useState[0],
    setWindowDimensions = _useState[1];
  useEffect(function () {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return function () {
        return window.removeEventListener('resize', handleResize);
      };
    }
  }, []);
  return windowDimensions;
};
var ConditionalWrapper = function ConditionalWrapper(_ref) {
  var condition = _ref.condition,
    wrapper = _ref.wrapper,
    children = _ref.children;
  return condition ? wrapper(children) : React.createElement(React.Fragment, null, children);
};
var packagesToCheck = /*#__PURE__*/new Set();
var checkTimeoutId;
function checkAndWarn() {
  var missingImports = Array.from(packagesToCheck).filter(function (namespace) {
    return parseInt(window.getComputedStyle(document.documentElement).getPropertyValue("--eds-" + namespace)) !== 1;
  }).sort();
  // Finally, we warn about those pesky imports
  var singleMissingImport = missingImports.length === 1;
  process.env.NODE_ENV !== "production" ? warning(missingImports.length === 0, "You are missing " + (singleMissingImport ? 'a CSS import' : missingImports.length + " CSS imports") + "!\n\nPlease add the following CSS import" + (singleMissingImport ? '' : 's') + " somewhere in your app:\n\n" + missingImports.map(function (namespace) {
    return "\t@import '@entur/" + namespace + "/dist/styles.css';";
  }).join('\n') + "\n") : void 0;
}
/** Warns the developer if they have forgotten to include styles */
function warnAboutMissingStyles() {
  var _process, _process$env;
  // We skip this check in production, and when we build static sites
  if (!(process.env.NODE_ENV !== "production") || typeof window === 'undefined' || typeof process !== 'undefined' && ((_process = process) == null ? void 0 : (_process$env = _process.env) == null ? void 0 : _process$env.TEST) === 'true') {
    return;
  }
  // First, let's clear earlier calls to setTimeout
  window.clearTimeout(checkTimeoutId);
  // Next, let's add all namespaces to the set of packages to check
  for (var _len = arguments.length, namespaces = new Array(_len), _key = 0; _key < _len; _key++) {
    namespaces[_key] = arguments[_key];
  }
  namespaces.forEach(function (namespace) {
    return packagesToCheck.add(namespace);
  });
  // Finally. let's trigger a run of the checker.
  checkTimeoutId = window.setTimeout(checkAndWarn, 1000);
}
export { ConditionalWrapper, mergeRefs, useDebounce, useForceUpdate, useOnClickOutside, useOnEscape, useOnMount, useRandomId, useWindowDimensions, warnAboutMissingStyles };